import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { accountingAPI, salesAPI, reportAPI } from '../utils/api';
import { Landmark, Wallet, ArrowDownLeft, ArrowUpRight, ArrowLeftRight, Plus, Edit2, Trash2, Users, Coins, FileText, Banknote, AlertTriangle, TrendingUp, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import useAuthStore from '../store/authStore';
import Modal from '../components/common/Modal';
import { fmt, money, cur, apiError, today, downloadBlob } from '../utils/helpers';
import SectionHero from '../components/common/SectionHero';

export default function Accounting() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const canEdit = ['admin', 'manager'].includes(user?.role);
  const canSuppliers = ['admin', 'manager', 'production_manager'].includes(user?.role);
  const [tab, setTab] = useState('overview');
  const [data, setData] = useState(null);

  const load = () => Promise.all([accountingAPI.summary(), accountingAPI.accounts(), accountingAPI.currencies(), accountingAPI.suppliers(), salesAPI.listCustomers()])
    .then(([s, a, c, sp, cu]) => setData({ summary: s.data, accounts: a.data, currencies: c.data, suppliers: sp.data, customers: cu.data }))
    .catch(() => toast.error(t('error')));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const tabs = [['overview', Landmark, 'accounts_overview'], ['pnl', TrendingUp, 'profit_loss'], ['transactions', ArrowLeftRight, 'receipts_payments'], ['suppliers', Users, 'suppliers'], ['currencies', Coins, 'currencies']];
  return (
    <div className="space-y-5">
      <SectionHero title={t('accounting')} />
      <div className="tabs">{tabs.map(([k, Icon, l]) => <button key={k} className={`tab ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}><Icon size={14} /> {t(l)}</button>)}</div>
      {!data ? <div className="loading-center"><div className="spinner" /></div> : (
        <>
          {tab === 'overview' && <Overview t={t} data={data} canEdit={canEdit} reload={load} />}
          {tab === 'pnl' && <ProfitLoss t={t} />}
          {tab === 'transactions' && <Transactions t={t} data={data} canEdit={canEdit} reload={load} />}
          {tab === 'suppliers' && <Suppliers t={t} data={data} canEdit={canSuppliers} canDelete={canEdit} reload={load} />}
          {tab === 'currencies' && <Currencies t={t} data={data} canEdit={canEdit} reload={load} />}
        </>
      )}
    </div>
  );
}

function Stat({ label, value, color, icon: Icon, sub }) {
  return (
    <div className="stat-card">
      <div><p className="stat-label">{label}</p><p className="stat-value" style={{ color, fontSize: 24 }}>{value}</p>{sub && <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{sub}</p>}</div>
      <div className="stat-icon" style={{ background: 'var(--accent-dim)' }}><Icon size={20} style={{ color: color || 'var(--accent)' }} /></div>
    </div>
  );
}

// ======================================================================= overview + accounts
function Overview({ t, data, canEdit, reload }) {
  const { summary: s, accounts, currencies } = data;
  const [modal, setModal] = useState(null);
  const [stmt, setStmt] = useState(null);
  const [saving, setSaving] = useState(false);

  const openStmt = a => accountingAPI.statement(a.id).then(r => setStmt(r.data)).catch(() => toast.error(t('error')));
  const save = async () => {
    if (!modal.name.trim() || !modal.currency_code) return toast.error(t('fill_required'));
    setSaving(true);
    try {
      const body = { name: modal.name, account_type: modal.account_type, opening_balance: parseFloat(modal.opening_balance) || 0, bank_details: modal.bank_details, notes: modal.notes };
      if (modal.id) await accountingAPI.updateAccount(modal.id, body);
      else await accountingAPI.createAccount({ ...body, currency_code: modal.currency_code });
      toast.success(t('saved')); setModal(null); reload();
    } catch (e) { toast.error(apiError(e, t)); } finally { setSaving(false); }
  };
  const del = async a => {
    if (!window.confirm(t('confirm_delete'))) return;
    try { await accountingAPI.deleteAccount(a.id); toast.success(t('deleted')); reload(); } catch (e) { toast.error(apiError(e, t)); }
  };

  return (
    <div className="space-y-5">
      {s.missing_rate && <div className="alert alert-warning"><AlertTriangle size={15} /><span>{t('missing_rate_warning')}</span></div>}
      <div className="grid-4">
        <Stat label={t('cash_total')} value={money(s.cash_usd)} color="#34d399" icon={Wallet} sub={Object.entries(s.cash_by_currency).map(([c, v]) => cur(v, c)).join(' · ')} />
        <Stat label={t('receivable')} value={money(s.receivable_usd)} color="#fbbf24" icon={ArrowDownLeft} />
        <Stat label={t('payable')} value={money(s.payable_usd)} color="#f87171" icon={ArrowUpRight} />
        <Stat label={t('this_month')} value={money(s.month_in_usd - s.month_out_usd)} icon={Banknote} sub={`↓ ${money(s.month_in_usd)}  ↑ ${money(s.month_out_usd)}`} />
      </div>
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="card-header" style={{ padding: '14px 20px', margin: 0 }}>
          <span className="card-title">{t('cash_bank_accounts')}</span>
          {canEdit && <button className="btn btn-primary btn-sm" onClick={() => setModal({ name: '', account_type: 'cash', currency_code: 'USD', opening_balance: 0, bank_details: '', notes: '' })}><Plus size={13} /> {t('add_account')}</button>}
        </div>
        {accounts.length === 0 ? <div className="empty-state"><Landmark size={32} style={{ color: 'var(--text-muted)', margin: '0 auto 8px' }} /><p>{t('no_accounts_hint')}</p></div> : (
          <div className="table-wrap"><table>
            <thead><tr><th>{t('name')}</th><th>{t('type')}</th><th>{t('currency')}</th><th>{t('balance')}</th><th className="hide-mobile">{t('usd_equivalent')}</th><th>{t('actions')}</th></tr></thead>
            <tbody>{accounts.map(a => (
              <tr key={a.id}>
                <td style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{a.name}{a.bank_details && <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{a.bank_details}</div>}</td>
                <td><span className={`badge ${a.account_type === 'bank' ? 'badge-blue' : 'badge-green'}`}>{t('acc_' + a.account_type)}</span></td>
                <td>{a.currency_code}</td>
                <td style={{ fontWeight: 700, color: a.balance < 0 ? '#f87171' : 'var(--text-primary)' }}>{cur(a.balance, a.currency_code)}</td>
                <td className="hide-mobile">{a.balance_usd != null ? money(a.balance_usd) : <span className="badge badge-yellow">{t('rate_missing')}</span>}</td>
                <td><div className="flex gap-1">
                  <button className="btn btn-ghost btn-sm" onClick={() => openStmt(a)}><FileText size={13} /> {t('statement')}</button>
                  {canEdit && <button className="btn btn-ghost btn-sm btn-icon" onClick={() => setModal({ ...a })}><Edit2 size={13} /></button>}
                  {canEdit && <button className="btn btn-ghost btn-sm btn-icon" style={{ color: '#f87171' }} onClick={() => del(a)}><Trash2 size={13} /></button>}
                </div></td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </div>

      {modal && (
        <Modal title={modal.id ? t('edit_account') : t('add_account')} onClose={() => setModal(null)}
          footer={<><button className="btn btn-secondary" onClick={() => setModal(null)}>{t('cancel')}</button><button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? t('loading') : t('save')}</button></>}>
          <div className="form-group"><label className="form-label">{t('name')} *</label><input className="input" value={modal.name} placeholder={t('account_name_placeholder')} onChange={e => setModal(m => ({ ...m, name: e.target.value }))} /></div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">{t('type')}</label>
              <select className="select" value={modal.account_type} onChange={e => setModal(m => ({ ...m, account_type: e.target.value }))}>
                <option value="cash">{t('acc_cash')}</option><option value="bank">{t('acc_bank')}</option>
              </select></div>
            <div className="form-group"><label className="form-label">{t('currency')}</label>
              <select className="select" disabled={!!modal.id} value={modal.currency_code} onChange={e => setModal(m => ({ ...m, currency_code: e.target.value }))}>
                {currencies.map(c => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
              </select></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">{t('opening_balance')}</label><input className="input" type="number" step="any" value={modal.opening_balance} onChange={e => setModal(m => ({ ...m, opening_balance: e.target.value }))} /></div>
            <div className="form-group"><label className="form-label">{t('bank_details')}</label><input className="input" value={modal.bank_details || ''} onChange={e => setModal(m => ({ ...m, bank_details: e.target.value }))} /></div>
          </div>
        </Modal>
      )}

      {stmt && (
        <Modal title={`${t('statement')} — ${stmt.account.name}`} size="modal-lg" onClose={() => setStmt(null)}>
          <div className="flex gap-3 mb-3 flex-wrap">
            <span className="badge badge-blue" style={{ fontSize: 12, padding: '5px 12px' }}>{t('opening_balance')}: {cur(stmt.opening, stmt.account.currency_code)}</span>
            <span className="badge badge-green" style={{ fontSize: 12, padding: '5px 12px' }}>{t('closing_balance')}: {cur(stmt.closing, stmt.account.currency_code)}</span>
          </div>
          {stmt.rows.length === 0 ? <div className="empty-state"><p>{t('no_data')}</p></div> : (
            <div className="table-wrap"><table>
              <thead><tr><th>{t('date')}</th><th>{t('category')}</th><th>{t('description')}</th><th>{t('in')}</th><th>{t('out')}</th><th>{t('balance')}</th></tr></thead>
              <tbody>{stmt.rows.map(r => (
                <tr key={r.id}>
                  <td>{r.tx_date}</td><td>{t('cat_' + r.category)}</td>
                  <td>{r.description || r.customer || r.supplier || '—'}</td>
                  <td style={{ color: '#34d399' }}>{r.direction === 'in' ? fmt(r.amount) : ''}</td>
                  <td style={{ color: '#f87171' }}>{r.direction === 'out' ? fmt(r.amount) : ''}</td>
                  <td style={{ fontWeight: 700 }}>{fmt(r.balance)}</td>
                </tr>
              ))}</tbody>
            </table></div>
          )}
        </Modal>
      )}
    </div>
  );
}

// ======================================================================= receipts / payments / transfers
const emptyTx = () => ({ direction: 'in', account_id: '', amount: '', per_usd: '', category: 'income', expense_category: '', customer_id: '', supplier_id: '', tx_date: today(), description: '' });

function Transactions({ t, data, canEdit, reload }) {
  const { accounts, currencies, customers, suppliers } = data;
  const [rows, setRows] = useState(null);
  const [f, setF] = useState({ account_id: '', category: '', direction: '', date_from: '', date_to: '' });
  const [txModal, setTxModal] = useState(null);
  const [trModal, setTrModal] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadRows = () => accountingAPI.transactions(Object.fromEntries(Object.entries(f).filter(([, v]) => v))).then(r => setRows(r.data)).catch(() => toast.error(t('error')));
  useEffect(() => { setRows(null); loadRows(); }, [f]); // eslint-disable-line

  const rateFor = accId => { const a = accounts.find(x => x.id === accId); return currencies.find(c => c.code === a?.currency_code)?.per_usd || ''; };
  const accCur = accId => accounts.find(x => x.id === accId)?.currency_code;
  const openTx = direction => {
    const a = accounts[0];
    setTxModal({ ...emptyTx(), direction, category: direction === 'in' ? 'income' : 'expense', account_id: a?.id || '', per_usd: a ? rateFor(a.id) : '' });
  };
  const saveTx = async () => {
    const m = txModal;
    if (!m.account_id || !(parseFloat(m.amount) > 0)) return toast.error(t('fill_required'));
    if (!(parseFloat(m.per_usd) > 0)) return toast.error(t('rate_missing'));
    setSaving(true);
    try {
      await accountingAPI.createTransaction({ ...m, amount: parseFloat(m.amount), per_usd: parseFloat(m.per_usd), customer_id: m.customer_id || null, supplier_id: m.supplier_id || null,
        expense_category: m.category === 'expense' ? m.expense_category || null : null });
      toast.success(t('saved')); setTxModal(null); loadRows(); reload();
    } catch (e) { toast.error(apiError(e, t)); } finally { setSaving(false); }
  };
  const saveTr = async () => {
    const m = trModal;
    if (!m.from_account_id || !m.to_account_id || !(parseFloat(m.amount_from) > 0)) return toast.error(t('fill_required'));
    if (m.from_account_id === m.to_account_id) return toast.error(t('same_account_error'));
    setSaving(true);
    try {
      await accountingAPI.transfer({ ...m, amount_from: parseFloat(m.amount_from), amount_to: m.amount_to ? parseFloat(m.amount_to) : null });
      toast.success(t('saved')); setTrModal(null); loadRows(); reload();
    } catch (e) { toast.error(apiError(e, t)); } finally { setSaving(false); }
  };
  const del = async r => {
    if (!window.confirm(r.transfer_group ? t('confirm_delete_transfer') : t('confirm_delete'))) return;
    try { await accountingAPI.deleteTransaction(r.id); toast.success(t('deleted')); loadRows(); reload(); } catch (e) { toast.error(apiError(e, t)); }
  };
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const totalIn = (rows || []).filter(r => r.direction === 'in' && r.category !== 'transfer').reduce((s, r) => s + r.amount_usd, 0);
  const totalOut = (rows || []).filter(r => r.direction === 'out' && r.category !== 'transfer').reduce((s, r) => s + r.amount_usd, 0);
  const trUsd = trModal && parseFloat(trModal.amount_from) > 0 && rateFor(trModal.from_account_id) ? parseFloat(trModal.amount_from) / rateFor(trModal.from_account_id) : null;

  return (
    <div className="space-y-5">
      {canEdit && accounts.length > 0 && (
        <div className="flex gap-2 flex-wrap justify-end">
          <button className="btn btn-sm" style={{ background: 'var(--green-dim)', color: '#34d399', border: '1px solid rgba(16,185,129,0.25)' }} onClick={() => openTx('in')}><ArrowDownLeft size={14} /> {t('new_receipt')}</button>
          <button className="btn btn-danger btn-sm" onClick={() => openTx('out')}><ArrowUpRight size={14} /> {t('new_payment')}</button>
          <button className="btn btn-secondary btn-sm" onClick={() => setTrModal({ from_account_id: accounts[0].id, to_account_id: accounts[1]?.id || '', amount_from: '', amount_to: '', tx_date: today(), description: '' })}><ArrowLeftRight size={14} /> {t('account_transfer')}</button>
        </div>
      )}
      {accounts.length === 0 && <div className="alert alert-warning"><span>{t('no_accounts_hint')}</span></div>}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
          <select className="select" value={f.account_id} onChange={e => set('account_id', e.target.value)}><option value="">{t('all_accounts')}</option>{accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}</select>
          <select className="select" value={f.direction} onChange={e => set('direction', e.target.value)}><option value="">{t('in')} + {t('out')}</option><option value="in">{t('in')}</option><option value="out">{t('out')}</option></select>
          <select className="select" value={f.category} onChange={e => set('category', e.target.value)}><option value="">{t('all_categories')}</option>{['customer_receipt', 'supplier_payment', 'expense', 'income', 'transfer', 'other'].map(c => <option key={c} value={c}>{t('cat_' + c)}</option>)}</select>
          <input className="input" type="date" value={f.date_from} onChange={e => set('date_from', e.target.value)} />
          <input className="input" type="date" value={f.date_to} onChange={e => set('date_to', e.target.value)} />
        </div>
        {rows && rows.length > 0 && <div style={{ padding: '10px 20px', display: 'flex', gap: 10, flexWrap: 'wrap', borderBottom: '1px solid var(--border)' }}>
          <span className="badge badge-green" style={{ fontSize: 12, padding: '4px 12px' }}>{t('in')}: {money(totalIn)}</span>
          <span className="badge badge-red" style={{ fontSize: 12, padding: '4px 12px' }}>{t('out')}: {money(totalOut)}</span>
        </div>}
        {!rows ? <div className="loading-center"><div className="spinner" /></div> : rows.length === 0 ? <div className="empty-state"><p>{t('no_data')}</p></div> : (
          <div className="table-wrap"><table>
            <thead><tr><th>{t('date')}</th><th>{t('category')}</th><th>{t('account')}</th><th>{t('amount')}</th><th className="hide-mobile">USD</th><th>{t('party')}</th><th className="hide-mobile">{t('description')}</th>{canEdit && <th></th>}</tr></thead>
            <tbody>{rows.map(r => (
              <tr key={r.id}>
                <td style={{ whiteSpace: 'nowrap' }}>{r.tx_date}</td>
                <td><span className={`badge ${r.direction === 'in' ? 'badge-green' : 'badge-red'}`}>{r.direction === 'in' ? '↓' : '↑'} {t('cat_' + r.category)}</span>{r.expense_category && <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{r.expense_category}</div>}</td>
                <td>{r.account}</td>
                <td style={{ fontWeight: 700, color: r.direction === 'in' ? '#34d399' : '#f87171', whiteSpace: 'nowrap' }}>{r.direction === 'in' ? '+' : '−'}{cur(r.amount, r.currency_code)}</td>
                <td className="hide-mobile">{money(r.amount_usd)}</td>
                <td>{r.customer || r.supplier || '—'}{(r.sale_number || r.purchase_number) && <div className="text-xs" style={{ color: 'var(--accent)' }}>{r.sale_number || r.purchase_number}</div>}</td>
                <td className="hide-mobile" style={{ color: 'var(--text-muted)' }}>{r.description || '—'}</td>
                {canEdit && <td><button className="btn btn-ghost btn-sm btn-icon" style={{ color: '#f87171' }} onClick={() => del(r)}><Trash2 size={13} /></button></td>}
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </div>

      {txModal && (
        <Modal title={txModal.direction === 'in' ? t('new_receipt') : t('new_payment')} onClose={() => setTxModal(null)}
          footer={<><button className="btn btn-secondary" onClick={() => setTxModal(null)}>{t('cancel')}</button><button className="btn btn-primary" onClick={saveTx} disabled={saving}>{saving ? t('loading') : t('save')}</button></>}>
          <div className="form-row">
            <div className="form-group"><label className="form-label">{t('account')} *</label>
              <select className="select" value={txModal.account_id} onChange={e => setTxModal(m => ({ ...m, account_id: e.target.value, per_usd: rateFor(e.target.value) }))}>
                {accounts.map(a => <option key={a.id} value={a.id}>{a.name} ({a.currency_code})</option>)}
              </select></div>
            <div className="form-group"><label className="form-label">{t('category')}</label>
              <select className="select" value={txModal.category} onChange={e => setTxModal(m => ({ ...m, category: e.target.value }))}>
                {(txModal.direction === 'in' ? ['income', 'customer_receipt', 'other'] : ['expense', 'supplier_payment', 'other']).map(c => <option key={c} value={c}>{t('cat_' + c)}</option>)}
              </select></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">{t('amount')} ({accCur(txModal.account_id)}) *</label><input className="input" type="number" min="0" step="any" value={txModal.amount} onChange={e => setTxModal(m => ({ ...m, amount: e.target.value }))} /></div>
            <div className="form-group"><label className="form-label">{t('rate_per_usd')}</label><input className="input" type="number" min="0" step="any" disabled={accCur(txModal.account_id) === 'USD'} value={txModal.per_usd} onChange={e => setTxModal(m => ({ ...m, per_usd: e.target.value }))} /></div>
          </div>
          {txModal.category === 'expense' && <div className="form-group"><label className="form-label">{t('expense_category')}</label>
            <input className="input" list="exp-cats" value={txModal.expense_category} onChange={e => setTxModal(m => ({ ...m, expense_category: e.target.value }))} />
            <datalist id="exp-cats">{t('expense_categories_list').split(',').map(x => <option key={x} value={x} />)}</datalist></div>}
          {txModal.category === 'customer_receipt' && <div className="form-group"><label className="form-label">{t('customer')}</label>
            <select className="select" value={txModal.customer_id} onChange={e => setTxModal(m => ({ ...m, customer_id: e.target.value }))}><option value="">—</option>{customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{t('receipt_invoice_hint')}</p></div>}
          {txModal.category === 'supplier_payment' && <div className="form-group"><label className="form-label">{t('supplier')}</label>
            <select className="select" value={txModal.supplier_id} onChange={e => setTxModal(m => ({ ...m, supplier_id: e.target.value }))}><option value="">—</option>{suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{t('payment_invoice_hint')}</p></div>}
          <div className="form-row">
            <div className="form-group"><label className="form-label">{t('date')}</label><input className="input" type="date" value={txModal.tx_date} onChange={e => setTxModal(m => ({ ...m, tx_date: e.target.value }))} /></div>
            <div className="form-group"><label className="form-label">{t('description')}</label><input className="input" value={txModal.description} onChange={e => setTxModal(m => ({ ...m, description: e.target.value }))} /></div>
          </div>
        </Modal>
      )}

      {trModal && (
        <Modal title={t('account_transfer')} onClose={() => setTrModal(null)}
          footer={<><button className="btn btn-secondary" onClick={() => setTrModal(null)}>{t('cancel')}</button><button className="btn btn-primary" onClick={saveTr} disabled={saving}>{saving ? t('loading') : t('save')}</button></>}>
          <div className="form-row">
            <div className="form-group"><label className="form-label">{t('from_account')}</label>
              <select className="select" value={trModal.from_account_id} onChange={e => setTrModal(m => ({ ...m, from_account_id: e.target.value }))}>{accounts.map(a => <option key={a.id} value={a.id}>{a.name} ({a.currency_code})</option>)}</select></div>
            <div className="form-group"><label className="form-label">{t('to_account')}</label>
              <select className="select" value={trModal.to_account_id} onChange={e => setTrModal(m => ({ ...m, to_account_id: e.target.value }))}><option value="">—</option>{accounts.map(a => <option key={a.id} value={a.id}>{a.name} ({a.currency_code})</option>)}</select></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">{t('amount_sent')} ({accCur(trModal.from_account_id)})</label><input className="input" type="number" min="0" step="any" value={trModal.amount_from} onChange={e => setTrModal(m => ({ ...m, amount_from: e.target.value }))} /></div>
            {accCur(trModal.from_account_id) !== accCur(trModal.to_account_id) && trModal.to_account_id && (
              <div className="form-group"><label className="form-label">{t('amount_received')} ({accCur(trModal.to_account_id)})</label>
                <input className="input" type="number" min="0" step="any" placeholder={trUsd && rateFor(trModal.to_account_id) ? fmt(trUsd * rateFor(trModal.to_account_id), 0) : ''} value={trModal.amount_to} onChange={e => setTrModal(m => ({ ...m, amount_to: e.target.value }))} />
                <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{t('auto_convert_hint')}</p></div>
            )}
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">{t('date')}</label><input className="input" type="date" value={trModal.tx_date} onChange={e => setTrModal(m => ({ ...m, tx_date: e.target.value }))} /></div>
            <div className="form-group"><label className="form-label">{t('description')}</label><input className="input" value={trModal.description} onChange={e => setTrModal(m => ({ ...m, description: e.target.value }))} /></div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ======================================================================= suppliers
function Suppliers({ t, data, canEdit, canDelete, reload }) {
  const [modal, setModal] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!modal.name.trim()) return toast.error(t('name_required'));
    setSaving(true);
    try {
      const body = { name: modal.name, phone: modal.phone, address: modal.address, notes: modal.notes };
      if (modal.id) await accountingAPI.updateSupplier(modal.id, body); else await accountingAPI.createSupplier(body);
      toast.success(t('saved')); setModal(null); reload();
    } catch (e) { toast.error(apiError(e, t)); } finally { setSaving(false); }
  };
  const del = async s => {
    if (!window.confirm(t('confirm_delete'))) return;
    try { await accountingAPI.deleteSupplier(s.id); toast.success(t('deleted')); reload(); } catch (e) { toast.error(apiError(e, t)); }
  };
  const openLedger = s => accountingAPI.ledger({ supplier_id: s.id }).then(r => setLedger(r.data)).catch(() => toast.error(t('error')));
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div className="card-header" style={{ padding: '14px 20px', margin: 0 }}>
        <span className="card-title">{t('suppliers')}</span>
        {canEdit && <button className="btn btn-primary btn-sm" onClick={() => setModal({ name: '', phone: '', address: '', notes: '' })}><Plus size={13} /> {t('add_supplier')}</button>}
      </div>
      {data.suppliers.length === 0 ? <div className="empty-state"><Users size={32} style={{ color: 'var(--text-muted)', margin: '0 auto 8px' }} /><p>{t('no_data')}</p></div> : (
        <div className="table-wrap"><table>
          <thead><tr><th>{t('name')}</th><th>{t('phone')}</th><th>{t('total_purchases')}</th><th>{t('debt')}</th><th>{t('actions')}</th></tr></thead>
          <tbody>{data.suppliers.map(s => (
            <tr key={s.id}>
              <td style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{s.name}</td>
              <td dir="ltr">{s.phone || '—'}</td>
              <td>{money(s.total_purchases_usd)}</td>
              <td style={{ color: s.debt_usd > 0 ? '#f87171' : 'var(--text-muted)', fontWeight: s.debt_usd > 0 ? 700 : 400 }}>{money(s.debt_usd)}</td>
              <td><div className="flex gap-1">
                <button className="btn btn-ghost btn-sm" onClick={() => openLedger(s)}><FileText size={13} /> {t('ledger')}</button>
                {canEdit && <button className="btn btn-ghost btn-sm btn-icon" onClick={() => setModal({ ...s })}><Edit2 size={13} /></button>}
                {canDelete && <button className="btn btn-ghost btn-sm btn-icon" style={{ color: '#f87171' }} onClick={() => del(s)}><Trash2 size={13} /></button>}
              </div></td>
            </tr>
          ))}</tbody>
        </table></div>
      )}
      {modal && (
        <Modal title={modal.id ? t('edit_supplier') : t('add_supplier')} onClose={() => setModal(null)}
          footer={<><button className="btn btn-secondary" onClick={() => setModal(null)}>{t('cancel')}</button><button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? t('loading') : t('save')}</button></>}>
          <div className="form-group"><label className="form-label">{t('name')} *</label><input className="input" value={modal.name} onChange={e => setModal(m => ({ ...m, name: e.target.value }))} /></div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">{t('phone')}</label><input className="input" dir="ltr" value={modal.phone || ''} onChange={e => setModal(m => ({ ...m, phone: e.target.value }))} /></div>
            <div className="form-group"><label className="form-label">{t('address')}</label><input className="input" value={modal.address || ''} onChange={e => setModal(m => ({ ...m, address: e.target.value }))} /></div>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">{t('notes')}</label><textarea className="input" style={{ minHeight: 56 }} value={modal.notes || ''} onChange={e => setModal(m => ({ ...m, notes: e.target.value }))} /></div>
        </Modal>
      )}
      {ledger && <LedgerModal t={t} ledger={ledger} onClose={() => setLedger(null)} />}
    </div>
  );
}

export function LedgerModal({ t, ledger, onClose }) {
  return (
    <Modal title={`${t('ledger')} — ${ledger.party}`} size="modal-lg" onClose={onClose}>
      <div className="mb-3"><span className={`badge ${ledger.balance > 0 ? 'badge-red' : 'badge-green'}`} style={{ fontSize: 13, padding: '5px 14px' }}>{t('balance')}: {money(ledger.balance)}</span></div>
      {ledger.entries.length === 0 ? <div className="empty-state"><p>{t('no_data')}</p></div> : (
        <div className="table-wrap"><table>
          <thead><tr><th>{t('date')}</th><th>{t('type')}</th><th>{t('reference')}</th><th>{t('debit')}</th><th>{t('credit')}</th><th>{t('balance')}</th></tr></thead>
          <tbody>{ledger.entries.map((e, i) => (
            <tr key={i}>
              <td>{e.date}</td><td>{t('led_' + e.type)}</td>
              <td>{e.ref || '—'}{e.note && <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{t('pay_' + e.note, e.note)}</div>}</td>
              <td>{e.debit ? money(e.debit) : ''}</td><td style={{ color: '#34d399' }}>{e.credit ? money(e.credit) : ''}</td>
              <td style={{ fontWeight: 700 }}>{money(e.balance)}</td>
            </tr>
          ))}</tbody>
        </table></div>
      )}
    </Modal>
  );
}

// ======================================================================= currencies
function Currencies({ t, data, canEdit, reload }) {
  const [rates, setRates] = useState(Object.fromEntries(data.currencies.map(c => [c.code, c.per_usd ?? ''])));
  const [add, setAdd] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hist, setHist] = useState(null);
  useEffect(() => { setRates(Object.fromEntries(data.currencies.map(c => [c.code, c.per_usd ?? '']))); }, [data.currencies]);
  const when = v => (v ? new Date(String(v).endsWith('Z') ? v : v + 'Z').toLocaleString('en-GB', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');
  const saveRate = async code => {
    const v = parseFloat(rates[code]);
    if (!(v > 0)) return toast.error(t('rate_missing'));
    try { await accountingAPI.updateCurrency(code, { per_usd: v }); toast.success(t('rate_saved_manual')); reload(); } catch (e) { toast.error(apiError(e, t)); }
  };
  const setAuto = async (code, on) => {
    try { await accountingAPI.updateCurrency(code, { auto_update: on }); toast.success(t('saved')); reload(); } catch (e) { toast.error(apiError(e, t)); }
  };
  const refresh = async () => {
    setBusy(true);
    try {
      const r = (await accountingAPI.refreshRates()).data;
      toast.success(`${t('rates_updated')}: ${r.updated.length ? r.updated.map(u => u.code).join(', ') : t('no_change')}`);
      if (r.not_found.length) toast.error(`${t('rate_not_in_source')}: ${r.not_found.join(', ')}`);
      reload();
    } catch (e) { toast.error(apiError(e, t)); } finally { setBusy(false); }
  };
  const openHist = async c => {
    try { setHist({ code: c.code, rows: (await accountingAPI.rateHistory(c.code)).data }); } catch { toast.error(t('error')); }
  };
  const saveNew = async () => {
    if (!add.code.trim() || !add.name.trim()) return toast.error(t('fill_required'));
    try {
      await accountingAPI.createCurrency({ code: add.code, name: add.name, per_usd: parseFloat(add.per_usd) || null, auto_update: add.auto_update });
      toast.success(t('saved')); setAdd(null); reload();
    } catch (e) { toast.error(apiError(e, t)); }
  };
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div className="card-header" style={{ padding: '14px 20px', margin: 0 }}>
        <span className="card-title">{t('currencies')}</span>
        {canEdit && <div className="flex gap-2">
          <button className="btn btn-secondary btn-sm" onClick={refresh} disabled={busy}>{busy ? t('loading') : '⟳ ' + t('update_rates_now')}</button>
          <button className="btn btn-primary btn-sm" onClick={() => setAdd({ code: '', name: '', per_usd: '', auto_update: true })}><Plus size={13} /> {t('add_currency')}</button>
        </div>}
      </div>
      <p className="text-xs" style={{ padding: '10px 20px 0', color: 'var(--text-muted)', lineHeight: 1.8 }}>{t('rate_help_auto')}</p>
      <div className="table-wrap"><table>
        <thead><tr><th>{t('currency')}</th><th>{t('rate_per_usd')}</th><th>{t('auto_update')}</th><th className="hide-mobile">{t('last_update')}</th>{canEdit && <th></th>}</tr></thead>
        <tbody>{data.currencies.map(c => (
          <tr key={c.code}>
            <td style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{c.code} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>— {c.name}</span>{c.is_base && <span className="badge badge-blue" style={{ marginInlineStart: 8 }}>{t('base')}</span>}</td>
            <td>{c.is_base ? '1' : canEdit
              ? <input className="input" style={{ width: 170, padding: '6px 10px' }} type="number" min="0" step="any" value={rates[c.code]} placeholder={t('rate_missing')}
                       onChange={e => setRates(r => ({ ...r, [c.code]: e.target.value }))} />
              : (c.per_usd ? fmt(c.per_usd, 4) : <span className="badge badge-yellow">{t('rate_missing')}</span>)}</td>
            <td>{c.is_base ? '—' : (
              <label className="checkbox-wrap" style={{ cursor: canEdit ? 'pointer' : 'default' }}>
                <input type="checkbox" checked={c.auto_update} disabled={!canEdit} onChange={e => setAuto(c.code, e.target.checked)} />
                <span className={`badge ${c.auto_update ? 'badge-green' : 'badge-yellow'}`}>{c.auto_update ? t('auto') : t('manual')}</span>
              </label>)}</td>
            <td className="hide-mobile" style={{ color: 'var(--text-muted)', fontSize: 12 }}>
              {c.is_base ? '—' : <>{when(c.auto_update ? c.rate_fetched_at : c.updated_at)}<div style={{ fontSize: 10 }}>{c.rate_source === 'manual' ? t('manual') : c.rate_source || ''}</div></>}
            </td>
            {canEdit && <td>{!c.is_base && <div className="flex gap-1">
              <button className="btn btn-secondary btn-sm" onClick={() => saveRate(c.code)}>{t('save')}</button>
              <button className="btn btn-ghost btn-sm" onClick={() => openHist(c)}><FileText size={13} /> {t('history')}</button>
            </div>}</td>}
          </tr>
        ))}</tbody>
      </table></div>
      {hist && (
        <Modal title={`${t('rate_history')} — ${hist.code}`} onClose={() => setHist(null)} style={{ maxWidth: 460 }}>
          {hist.rows.length === 0 ? <div className="empty-state"><p>{t('no_data')}</p></div> : (
            <div className="table-wrap"><table>
              <thead><tr><th>{t('date')}</th><th>{t('rate_per_usd')}</th><th>{t('source')}</th></tr></thead>
              <tbody>{hist.rows.map((r, i) => <tr key={i}><td>{when(r.created_at)}</td><td>{fmt(r.per_usd, 4)}</td><td>{r.source === 'manual' ? t('manual') : r.source}</td></tr>)}</tbody>
            </table></div>
          )}
        </Modal>
      )}
      {add && (
        <Modal title={t('add_currency')} onClose={() => setAdd(null)} style={{ maxWidth: 420 }}
          footer={<><button className="btn btn-secondary" onClick={() => setAdd(null)}>{t('cancel')}</button><button className="btn btn-primary" onClick={saveNew}>{t('save')}</button></>}>
          <div className="form-row">
            <div className="form-group"><label className="form-label">{t('code')} *</label><input className="input" dir="ltr" maxLength={10} placeholder="AED" value={add.code} onChange={e => setAdd(a => ({ ...a, code: e.target.value.toUpperCase() }))} /></div>
            <div className="form-group"><label className="form-label">{t('name')} *</label><input className="input" value={add.name} onChange={e => setAdd(a => ({ ...a, name: e.target.value }))} /></div>
          </div>
          <label className="checkbox-wrap mb-3"><input type="checkbox" checked={add.auto_update} onChange={e => setAdd(a => ({ ...a, auto_update: e.target.checked }))} /><span>{t('auto_update')}</span></label>
          {!add.auto_update && <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">{t('rate_per_usd')}</label><input className="input" type="number" min="0" step="any" value={add.per_usd} onChange={e => setAdd(a => ({ ...a, per_usd: e.target.value }))} /></div>}
        </Modal>
      )}
    </div>
  );
}


// ======================================================================= profit & loss
function ProfitLoss({ t }) {
  const { i18n } = useTranslation();
  const [period, setPeriod] = useState('monthly');
  const [range, setRange] = useState({ date_from: '', date_to: '' });
  const [d, setD] = useState(null);
  const params = period === 'custom' ? { period, date_from: range.date_from || undefined, date_to: range.date_to || undefined } : { period };
  useEffect(() => {
    if (period === 'custom' && !range.date_from && !range.date_to) return;
    setD(null);
    reportAPI.pnl(params).then(r => setD(r.data)).catch(() => toast.error(t('error')));
  }, [period, range.date_from, range.date_to]); // eslint-disable-line
  const exportX = async () => {
    try {
      const r = await reportAPI.exportExcel({ report_type: 'pnl', lang: i18n.language, ...params });
      downloadBlob(r.data, `profit_loss_${today()}.xlsx`);
    } catch { toast.error(t('error')); }
  };
  const Row = ({ label, value, strong, neg, indent, color }) => (
    <tr>
      <td style={{ paddingInlineStart: indent ? 36 : 16, fontWeight: strong ? 700 : 400, color: strong ? 'var(--text-primary)' : undefined }}>{label}</td>
      <td style={{ fontWeight: strong ? 800 : 500, color: color || (neg ? '#f87171' : 'var(--text-primary)'), fontSize: strong ? 15 : undefined }}>
        {neg && value ? '− ' + money(Math.abs(value)) : money(value || 0)}
      </td>
    </tr>
  );
  return (
    <div className="space-y-5">
      <div className="flex gap-2 flex-wrap items-center">
        <select className="select" style={{ width: 150 }} value={period} onChange={e => setPeriod(e.target.value)}>
          {['daily', 'weekly', 'monthly', 'yearly', 'all', 'custom'].map(p => <option key={p} value={p}>{t(p === 'all' ? 'all_time' : p === 'custom' ? 'custom_range' : p)}</option>)}
        </select>
        {period === 'custom' && <>
          <input type="date" className="input" style={{ width: 150 }} value={range.date_from} onChange={e => setRange(r => ({ ...r, date_from: e.target.value }))} />
          <input type="date" className="input" style={{ width: 150 }} value={range.date_to} onChange={e => setRange(r => ({ ...r, date_to: e.target.value }))} />
        </>}
        <button className="btn btn-secondary" onClick={exportX}><Download size={14} /> {t('export_excel')}</button>
        {d && <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{String(d.date_from)} → {String(d.date_to)}</span>}
      </div>
      {!d ? <div className="loading-center"><div className="spinner" /></div> : (
        <>
          <div className="grid-3">
            <Stat label={t('revenue')} value={money(d.revenue)} color="#34d399" icon={TrendingUp} sub={`${d.sales_count} ${t('transactions')}`} />
            <Stat label={t('net_profit')} value={money(d.net_profit)} color={d.net_profit >= 0 ? '#34d399' : '#f87171'} icon={Landmark} />
            <Stat label={t('net_cash_flow')} value={money(d.net_cash)} color={d.net_cash >= 0 ? '#a5b4fc' : '#f87171'} icon={Wallet}
                  sub={`${t('cash_in')} ${money(d.cash_in)} · ${t('cash_out')} ${money(d.cash_out)}`} />
          </div>
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div className="card-header" style={{ padding: '14px 20px', margin: 0 }}><span className="card-title">{t('profit_loss')} (USD)</span></div>
            <div className="table-wrap"><table><tbody>
              <Row label={t('revenue')} value={d.revenue} strong color="#34d399" />
              <Row label={t('raw_material_purchases')} value={d.purchases} neg />
              <Row label={t('gross_profit')} value={d.gross_profit} strong color={d.gross_profit >= 0 ? '#34d399' : '#f87171'} />
              <Row label={t('customs_fees_bills')} value={d.customs_fees} neg />
              <Row label={t('expenses')} value={d.total_expenses} neg />
              {Object.entries(d.expenses).map(([k, v]) => <Row key={k} label={t('exp_' + k, k)} value={v} neg indent />)}
              <Row label={t('other_income')} value={d.other_income} color="#34d399" />
              <Row label={t('net_profit')} value={d.net_profit} strong color={d.net_profit >= 0 ? '#34d399' : '#f87171'} />
            </tbody></table></div>
          </div>
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{t('pnl_note')}</p>
        </>
      )}
    </div>
  );
}
